/**
 * Installs global native bridge objects in the window.
 */

import { ListenedMessage } from 'native_bridge/types'
import {
  callListener,
  listen,
  respondToPadletSettingsUpdate,
  respondToSettingsPanelHide,
  respondToPostCreation,
  respondToSignalToHideAllPanelsAndMenus,
} from 'native_bridge/listen'
import * as stateActions from 'native_bridge/native_actions_map'

interface NativeBridge {
  receiveMessage: Function
  addMessageHandler: Function
}

interface WallwisherWindow {
  ww?: { nativeBridge?: NativeBridge }
  $nativeBridge: unknown
  postMessage: Function
  location: { hostname: string }
}

interface ActionableStore {
  dispatch(fnName: string, ...args: any[]): void
}

export function installListeners (store: ActionableStore): void {
  // respond to changes in preview attributes
  respondToPadletSettingsUpdate((msg: unknown): void => {
    const msgWithPreviewAttributes = msg as { preview_attributes: unknown }
    store.dispatch('settings/updatePreviewAttributes', { ...msgWithPreviewAttributes.preview_attributes })
  })

  // hide the settings panel
  respondToSettingsPanelHide((): void => {
    store.dispatch('settings/hideSettingsPanel')
  })

  // create a post on the padlet
  respondToPostCreation((postAttributes: unknown): void => {
    store.dispatch('post/createPost', { attributes: postAttributes })
  })

  // hide all panels and menus that are being show
  respondToSignalToHideAllPanelsAndMenus((): void => {
    store.dispatch('nativeApp/hidePanelsAndMenus')
  })
}

export function install (window: WallwisherWindow, store: ActionableStore): void {
  const { ww } = window
  if (ww) {
    // The nativeBridge.receiveMessage call is used to accept
    // messages from the native apps, to change the state of the app.
    //
    // Here we hijack the global (and old) native bridge receiveMessage and
    // addMessageHandler function so we can run callListener message through
    // our new message receiver, which is an extension of our Vuex store.
    const nb = ww.nativeBridge
    if (nb) {
      const oldReceive = nb.receiveMessage.bind(nb)
      // look at app/assets/javascripts/3/surface/native_bridge.coffee to
      // see the code for the old functions.
      nb.receiveMessage = (message: ListenedMessage): void => {
        if (!callListener(message)) {
          oldReceive(message)
        }
      }
      nb.addMessageHandler = listen.bind(nb)
    }
  }

  // Native bridge for app communication
  window.$nativeBridge = {
    dispatch: (fnName: string, ...args: any[]): void => {
      store.dispatch(stateActions[fnName].apply(null, args))
    },
  }
}
